import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  claimHostRibbonOwnership,
  isHostRibbonOwner,
  publishHostRibbonState,
  retireHostRibbonOwner,
  type HostRibbonOwnerToken,
  type HostRibbonState,
} from '../../lib/hostRibbon';

export interface HostRibbonOwner {
  /** Publishes ribbon state; returns false while a newer route owns the ribbon. */
  publish: (state: HostRibbonState) => boolean;
  /** Tells a command handler whether its route may still act on ribbon clicks. */
  isCurrentOwner: () => boolean;
}

/**
 * Claims the native ribbon for the calling route while it is active.
 *
 * Ownership lifetime is deliberately separate from publication: state can be
 * republished as often as the route needs without the ribbon flickering, and
 * the hide is emitted once, only when the still-current owner steps down.
 */
export const useHostRibbonOwner = (label: string, active = true): HostRibbonOwner => {
  const [token, setToken] = useState<HostRibbonOwnerToken | null>(null);

  useEffect(() => {
    if (!active) {
      setToken(null);

      return undefined;
    }

    const claimed = claimHostRibbonOwnership(label);
    setToken(claimed);

    return () => {
      retireHostRibbonOwner(claimed);
    };
  }, [active, label]);

  const publish = useCallback(
    (state: HostRibbonState) => publishHostRibbonState(token, state),
    [token]
  );

  const isCurrentOwner = useCallback(() => isHostRibbonOwner(token), [token]);

  return useMemo(() => ({ publish, isCurrentOwner }), [isCurrentOwner, publish]);
};
